import type { TApiColumnMappingFieldType } from "@/apis/proda_finance";

export type TColumnMappingFieldConfig = {
    fieldType: TApiColumnMappingFieldType;
    label: string;
    required: boolean;
    glOnly?: boolean;
    conditionalSegment?: 2 | 4 | 5 | 6 | 7;
    /**
     * Offered only when the deal's business partner uses multi-criteria CoA
     * mapping. Always optional: a GL line with no value for it resolves through
     * the account's fallback mapping.
     */
    multiCriteriaOnly?: boolean;
};

export const TB_COLUMN_FIELDS: TColumnMappingFieldConfig[] = [
    { fieldType: "amount", label: "Amount", required: true },
    { fieldType: "details_notes", label: "Details Notes", required: false },
    { fieldType: "local_propcode", label: "Local Propcode", required: true },
    { fieldType: "ref", label: "Ref", required: false },
    { fieldType: "source_account_name", label: "Source Account Name", required: true },
    { fieldType: "source_account_number", label: "Source Account Number", required: true },
    {
        fieldType: "source_counterparty_code_s2",
        label: "Source Counterparty Code S2",
        required: true,
        conditionalSegment: 2,
    },
    {
        fieldType: "source_counterparty_code_s4",
        label: "Source Counterparty Code S4",
        required: true,
        conditionalSegment: 4,
    },
    {
        fieldType: "source_counterparty_code_s5",
        label: "Source Counterparty Code S5",
        required: true,
        conditionalSegment: 5,
    },
    {
        fieldType: "source_counterparty_code_s6",
        label: "Source Counterparty Code S6",
        required: true,
        conditionalSegment: 6,
    },
    {
        // Segment 7 (Deal) also resolves from the line's platform property code, so
        // its source column is mappable but never mandatory — unlike S2/S4/S5/S6.
        // Mirrors the `required=False` _FieldSpec in file_type_columns.py.
        fieldType: "source_counterparty_code_s7",
        label: "Source Counterparty Code S7",
        required: false,
        conditionalSegment: 7,
    },
];

export const GL_COLUMN_FIELDS: TColumnMappingFieldConfig[] = [
    { fieldType: "amount", label: "Amount", required: true },
    { fieldType: "date", label: "Date", required: true, glOnly: true },
    { fieldType: "period", label: "Period", required: true, glOnly: true },
    { fieldType: "details_notes", label: "Details Notes", required: false },
    { fieldType: "local_propcode", label: "Local Propcode", required: true },
    { fieldType: "ref", label: "Ref", required: false },
    { fieldType: "source_account_name", label: "Source Account Name", required: true },
    { fieldType: "source_account_number", label: "Source Account Number", required: true },
    {
        fieldType: "source_account_number_2",
        label: "Source Account Number 2",
        required: false,
        glOnly: true,
        multiCriteriaOnly: true,
    },
    {
        fieldType: "source_counterparty_code_s2",
        label: "Source Counterparty Code S2",
        required: true,
        conditionalSegment: 2,
    },
    {
        fieldType: "source_counterparty_code_s4",
        label: "Source Counterparty Code S4",
        required: true,
        conditionalSegment: 4,
    },
    {
        fieldType: "source_counterparty_code_s5",
        label: "Source Counterparty Code S5",
        required: true,
        conditionalSegment: 5,
    },
    {
        fieldType: "source_counterparty_code_s6",
        label: "Source Counterparty Code S6",
        required: true,
        conditionalSegment: 6,
    },
    {
        // Segment 7 (Deal): mappable but never mandatory — see the TB list above.
        fieldType: "source_counterparty_code_s7",
        label: "Source Counterparty Code S7",
        required: false,
        conditionalSegment: 7,
    },
];
